import { OcChatConfig } from '../../../types/oc';
import { getCookie } from '../../../utils/misc';

/**
 * OC API Client - Generic HTTP client for OC API requests
 */

export const CREST_DEFAULT_BASE_URL = 'https://api.connectedcommunity.org/api/v2.0';
export const OC_SITE_DEFAULT_BASE_URL = '/higherlogic/ocapi/oc';

/**
 * HTTP client configuration
 */
export interface OcApiClientConfig {
  baseUrl: string;
  headers: Record<string, string>;
  /** When true, unwrap oc-api `{ data: T }` envelope */
  unwrapDataEnvelope?: boolean;
}

export const isOcSiteTransport = (config?: OcChatConfig): boolean =>
  config?.apiTransport === 'ocSite';

/**
 * Antiforgery form token rendered by OC pages (`__HL-RequestVerificationToken`).
 */
export const getRequestVerificationToken = (): string => {
  if (typeof document === 'undefined') {
    return '';
  }

  const formTokenElement = document.getElementsByName(
    '__HL-RequestVerificationToken',
  )[0] as HTMLInputElement | undefined;
  return formTokenElement?.value ?? '';
};

export const hasOcSiteAntiforgery = (): boolean =>
  getRequestVerificationToken().trim() !== '';

/**
 * Build base API URL for OC API
 * Returns the base URL at the API version level
 * Controller names are included in individual endpoint paths
 * Allows override via config for development/testing
 */
export const buildOcApiBaseUrl = (config?: OcChatConfig): string => {
  if (config?.apiBaseUrl) {
    return config.apiBaseUrl;
  }

  if (isOcSiteTransport(config)) {
    return OC_SITE_DEFAULT_BASE_URL;
  }

  return CREST_DEFAULT_BASE_URL;
};

/**
 * Crest base URL used for Crest-only endpoints (e.g. WhoAmI) even when the widget
 * uses ocSite for chat.
 */
export const buildCrestApiBaseUrl = (config?: OcChatConfig): string => {
  if (config?.apiTransport !== 'ocSite' && config?.apiBaseUrl) {
    return config.apiBaseUrl;
  }

  return CREST_DEFAULT_BASE_URL;
};

/**
 * Check if the Crest tenant cookie is available.
 */
export const hasTenantCookie = (): boolean => getCookie('HLTenantKey') !== null;

/**
 * Check if OC auth cookies are available (logged-in community user).
 */
export const hasCookieAuth = (): boolean => {
  const tenantKey = getCookie('HLTenantKey');
  const authToken = getCookie('HLAuthToken');
  return tenantKey !== null && authToken !== null;
};

/**
 * True when the configured transport can resolve a tenant for privacy/chat calls.
 * - crest: iamKey or HLTenantKey cookie
 * - ocSite: antiforgery token present (tenant comes from OC host context)
 */
export const canResolveOcTenant = (config: OcChatConfig): boolean => {
  if (isOcSiteTransport(config)) {
    return hasOcSiteAntiforgery();
  }

  if (config.iamKey && config.iamKey.trim() !== '') {
    return true;
  }

  return hasTenantCookie();
};

/**
 * Check if authentication headers are available
 * Returns true if either iamKey is provided OR both HLTenantKey and HLAuthToken cookies exist
 */
export const hasAuthHeaders = (config: OcChatConfig): boolean => {
  if (config.iamKey && config.iamKey.trim() !== '') {
    return true;
  }

  return hasCookieAuth();
};

/**
 * Crest request headers (IAM or cookie-based).
 */
export const getCrestHeaders = (config: OcChatConfig): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (config.iamKey) {
    headers['HLIAMKey'] = config.iamKey;
  } else {
    const tenantKey = getCookie('HLTenantKey');
    const authToken = getCookie('HLAuthToken');

    if (tenantKey) {
      headers['HLTenantKey'] = tenantKey;
    }
    if (authToken) {
      headers['HLAuthToken'] = authToken;
    }
  }

  if (config.HL_Test) {
    headers['HL_Test'] = config.HL_Test;
  }

  return headers;
};

/**
 * Get request headers for the active transport.
 */
export const getOcHeaders = (config: OcChatConfig): Record<string, string> => {
  if (isOcSiteTransport(config)) {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      RequestVerificationFormToken: getRequestVerificationToken(),
    };

    if (config.HL_Test) {
      headers['HL_Test'] = config.HL_Test;
    }

    return headers;
  }

  return getCrestHeaders(config);
};

/**
 * Create an OC API client instance for the configured transport.
 */
export const createOcApiClient = (config: OcChatConfig): OcApiClientConfig => {
  return {
    baseUrl: buildOcApiBaseUrl(config),
    headers: getOcHeaders(config),
    unwrapDataEnvelope: isOcSiteTransport(config),
  };
};

/**
 * Crest-only client (WhoAmI and other Crest endpoints) when the widget uses ocSite for chat.
 */
export const createCrestApiClient = (config: OcChatConfig): OcApiClientConfig => {
  return {
    baseUrl: buildCrestApiBaseUrl(config),
    headers: getCrestHeaders(config),
    unwrapDataEnvelope: false,
  };
};

const throwIfNotOk = (response: Response, method: string, path: string): void => {
  if (response.ok) {
    return;
  }

  if (response.status === 401 || response.status === 403) {
    throw new Error('Unauthorized - User not authenticated');
  }
  throw new Error(`${method} ${path} failed: ${response.statusText}`);
};

const parseJsonBody = async <T>(
  response: Response,
  path: string,
  unwrapDataEnvelope?: boolean,
): Promise<T> => {
  const text = await response.text();
  if (!text) {
    return {} as T;
  }

  const parsed: unknown = JSON.parse(text);

  if (!unwrapDataEnvelope) {
    return parsed as T;
  }

  if (!parsed || typeof parsed !== 'object' || !('data' in parsed)) {
    throw new Error(`Malformed response body from ${path}`);
  }

  return (parsed as { data: T }).data;
};

/**
 * Generic GET request
 */
export const get = async <T>(
  client: OcApiClientConfig,
  path: string,
): Promise<T> => {
  const url = `${client.baseUrl}${path}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: client.headers,
  });

  throwIfNotOk(response, 'GET', path);
  return parseJsonBody<T>(response, path, client.unwrapDataEnvelope);
};

/**
 * Generic POST request
 */
export const post = async <T, D = unknown>(
  client: OcApiClientConfig,
  path: string,
  data?: D,
): Promise<T> => {
  const url = `${client.baseUrl}${path}`;

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: client.headers,
    body: data !== undefined ? JSON.stringify(data) : undefined,
  });

  throwIfNotOk(response, 'POST', path);
  return parseJsonBody<T>(response, path, client.unwrapDataEnvelope);
};

/**
 * Generic PUT request
 */
export const put = async <T, D = unknown>(
  client: OcApiClientConfig,
  path: string,
  data?: D,
): Promise<T> => {
  const url = `${client.baseUrl}${path}`;

  const response = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: client.headers,
    body: data !== undefined ? JSON.stringify(data) : undefined,
  });

  throwIfNotOk(response, 'PUT', path);
  return parseJsonBody<T>(response, path, client.unwrapDataEnvelope);
};

/**
 * Generic DELETE request
 */
export const del = async <T>(
  client: OcApiClientConfig,
  path: string,
): Promise<T> => {
  const url = `${client.baseUrl}${path}`;

  const response = await fetch(url, {
    method: 'DELETE',
    credentials: 'include',
    headers: client.headers,
  });

  throwIfNotOk(response, 'DELETE', path);
  return parseJsonBody<T>(response, path, client.unwrapDataEnvelope);
};

/**
 * Generic OPTIONS request
 */
export const options = async <T>(
  client: OcApiClientConfig,
  path: string,
): Promise<T> => {
  const url = `${client.baseUrl}${path}`;

  const response = await fetch(url, {
    method: 'OPTIONS',
    credentials: 'include',
    headers: client.headers,
  });

  throwIfNotOk(response, 'OPTIONS', path);
  return parseJsonBody<T>(response, path, client.unwrapDataEnvelope);
};
